import { useEffect, useState, useRef } from 'react';
import { LinhaCompletaField } from './LinhaCompletaField';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2 , AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { quantidadeLinhasTelefone } from '@/lib/quantidadeLinhasTelefone';
import { useDebounce } from '@/hooks/useDebounce';

interface LinhaData {
  ddd: string;
  linha: string;
  gb?: string | null;
  operadora_doadora?: string | null;
  tipo_chip?: string | null;
  // 🆕 Campos de aparelho
  aparelho_valor?: string | null;
  aparelho_modelo?: string | null;
  aparelho_parcelas?: string | null;
  delta?: string | number | null;
  delta_economico?: string | number | null;
  delta_literal?: string | number | null;
  delta_ativo?: string | number | null;
  delta_formula_sign?: string | null;
  faixa?: string | number | null;
  tabela?: string | number | null;
  plano_atual?: string | null;
  plano_novo?: string | null;
  valor_atual?: string | number | null;
  valor_novo?: string | number | null;
  ticket_atual?: string | number | null;
  ticket_novo?: string | number | null;
  receita_adicionada?: string | number | null;
}

interface MultiLinhasFieldProps {
  ativacaoChip?: boolean;
  /** TT e renovação mantêm o chip do cliente: a linha fica completa sem ele. */
  semChip?: boolean;
  quantidade: number;
  linhas: LinhaData[];
  onQuantidadeChange: (qtd: number) => void;
  onLinhasChange: (linhas: LinhaData[]) => void;
  isHA?: boolean;
  isFIBRA?: boolean;
  semLinhaFixa?: boolean;
  onSemLinhaFixaChange?: (value: boolean) => void;
  disabled?: boolean; //  Bloqueia edição pós-fechamento
  quantidadeDaProposta?: boolean;
}

/**
 * Componente que renderiza múltiplos campos de linha telefônica
 * baseado na quantidade especificada, mantendo o design idêntico
 */
export function MultiLinhasField({
  quantidade: quantidadeProduto,
  linhas,
  onQuantidadeChange,
  onLinhasChange,
  isHA = false,
  ativacaoChip = false,
  semChip = false,
  isFIBRA = false,
  semLinhaFixa = false,
  onSemLinhaFixaChange,
  disabled = false,
  quantidadeDaProposta = false
}: MultiLinhasFieldProps) {
  const [quantidadeFixa, setQuantidadeFixa] = useState(() => quantidadeLinhasTelefone(quantidadeProduto, linhas, true));
  const quantidade = isFIBRA ? quantidadeFixa : quantidadeProduto;
  useEffect(() => {
    if (isFIBRA) setQuantidadeFixa(Math.max(1, linhas.length));
  }, [isFIBRA, linhas.length]);

  // Estado local simplificado
  const [linhasLocal, setLinhasLocal] = useState<LinhaData[]>(linhas);
  const [isSaving, setIsSaving] = useState(false);
  const ultimoSalvamentoRef = useRef<number>(0); //  Proteção race conditions

  // Debounce de 1200ms antes de salvar (mais tempo para usuário digitar)
  const linhasDebouncedValue = useDebounce(linhasLocal, 1200);

  // ✅ Sincronização INTELIGENTE - mesclar ao invés de sobrescrever
  useEffect(() => {
    if (isSaving) {
      console.log('⏸️ [SYNC] Salvamento em andamento, aguardando...');
      return;
    }

    const merged = Array.from({ length: quantidade }, (_, i) => {
      const propLinha = linhas[i] || { 
        ddd: '', 
        linha: '',
        gb: null,
        operadora_doadora: null, 
        tipo_chip: null,
        aparelho_valor: null,
        aparelho_modelo: null,
        aparelho_parcelas: null
      };
      
      const localLinha = linhasLocal[i];
      
      // ✅ MESCLAR: Priorizar dados locais preenchidos sobre props vazias
      if (localLinha) {
        return {
          ...propLinha,
          ...localLinha,
          ddd: localLinha.ddd || propLinha.ddd || '',
          linha: localLinha.linha || propLinha.linha || '',
          gb: localLinha.gb ?? propLinha.gb ?? null,
          operadora_doadora: localLinha.operadora_doadora || propLinha.operadora_doadora || null,
          tipo_chip: localLinha.tipo_chip || propLinha.tipo_chip || null,
          aparelho_valor: localLinha.aparelho_valor ?? propLinha.aparelho_valor ?? null,
          aparelho_modelo: localLinha.aparelho_modelo ?? propLinha.aparelho_modelo ?? null,
          aparelho_parcelas: localLinha.aparelho_parcelas ?? propLinha.aparelho_parcelas ?? null
        };
      }
      
      return propLinha;
    });
    
    const mergedString = JSON.stringify(merged);
    const localString = JSON.stringify(linhasLocal);
    
    if (mergedString !== localString) {
      console.log('📥 [SYNC] Props mudaram, MESCLANDO dados...');
      console.log('ANTES:', linhasLocal);
      console.log('PROPS:', linhas);
      console.log('DEPOIS (MERGED):', merged);
      setLinhasLocal(merged);
    }
  }, [linhas, quantidade, isSaving]);

  // ✅ Salvamento com proteção contra race conditions
  useEffect(() => {
    if (isSaving) return;
    
    const linhasOriginaisString = JSON.stringify(linhas);
    const linhasDebouncedString = JSON.stringify(linhasDebouncedValue);
    
    if (linhasDebouncedString !== linhasOriginaisString) {
      const agora = Date.now();
      
      // ✅ Evitar salvamentos múltiplos em menos de 1s
      if (agora - ultimoSalvamentoRef.current < 1000) {
        console.log('⏸️ [DEBOUNCE] Aguardando intervalo mínimo entre salvamentos');
        return;
      }
      
      console.log('💾 [DEBOUNCE] Salvando após 1200ms...');
      setIsSaving(true);
      ultimoSalvamentoRef.current = agora; //  Registrar timestamp
      onLinhasChange(linhasDebouncedValue);
      
      setTimeout(() => {
        setIsSaving(false);
        console.log('✅ [SAVE] Salvamento concluído');
      }, 1000);
    }
  }, [linhasDebouncedValue]);

  // Ajustar array de linhas quando quantidade mudar
  useEffect(() => {
    const qtdAtual = linhasLocal.length;
    
    if (qtdAtual < quantidade) {
      console.log(`➕ [QUANTIDADE] Adicionando ${quantidade - qtdAtual} linha(s)`);
      const novasLinhas = [...linhasLocal];
      for (let i = qtdAtual; i < quantidade; i++) {
        novasLinhas.push({ 
          ddd: '', 
          linha: isHA ? 'Número Novo' : '',
          gb: null,
          operadora_doadora: null, 
          tipo_chip: null,
          aparelho_valor: null,
          aparelho_modelo: null,
          aparelho_parcelas: null
        });
      }
      setLinhasLocal(novasLinhas);
    } else if (qtdAtual > quantidade) {
      console.log(`➖ [QUANTIDADE] Removendo ${qtdAtual - quantidade} linha(s)`);
      setLinhasLocal(linhasLocal.slice(0, quantidade));
    }
  }, [quantidade, isHA]);

  const handleQuantidadeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseInt(e.target.value);
    if (quantidadeDaProposta) return;
    if (!isNaN(valor) && valor >= 1 && valor <= 20) {
      if (isFIBRA) setQuantidadeFixa(valor);
      else onQuantidadeChange(valor);
    }
  };

  const updateLinha = (index: number, field: keyof LinhaData, value: string | null) => {
    const novasLinhas = [...linhasLocal];
    if (!novasLinhas[index]) {
      novasLinhas[index] = { ddd: '', linha: '', gb: null, operadora_doadora: null, tipo_chip: null, aparelho_valor: null, aparelho_modelo: null, aparelho_parcelas: null };
    }
    novasLinhas[index] = {
      ...novasLinhas[index],
      [field]: value
    };
    
    console.log(`🔄 UPDATE Linha ${index + 1} - ${field}:`, value);
    setLinhasLocal(novasLinhas);
  };

  return (
    <div className="space-y-4">
      {/* Campo Quantidade - Novo elemento discreto */}
      {!disabled && (
      <div className="flex items-center gap-3 bg-muted/20 rounded-lg p-3 border border-border/50">
        <Label className="text-sm font-medium whitespace-nowrap">
          {isFIBRA ? 'Quantidade de números fixos:' : 'Quantidade de Linhas:'}
        </Label>
        {quantidadeDaProposta ? (
          <span className="text-sm font-mono">{quantidade} · vem da proposta (mudar só em Revisar Proposta)</span>
        ) : (
        <Input
          type="number"
          min="1"
          max="20"
          value={quantidade}
          onChange={handleQuantidadeChange}
          onWheel={(e) => e.currentTarget.blur()}
          className="w-20 text-center font-mono"
        />
        )}
        {(() => {
          const linhasValidas = linhasLocal.filter(l => {
            const dddValido = l.ddd && l.ddd.length === 2;
            
            // Para produtos HA, "Número Novo" é válido
            const isNumeroNovo = l.linha?.toUpperCase() === 'NÚMERO NOVO';
            // ✅ FIBRA: 8 dígitos. Móvel: 8-9 dígitos.
            const linhaValida = isNumeroNovo || (isFIBRA 
              ? (l.linha && l.linha.length === 8) 
              : (l.linha && l.linha.length >= 8 && l.linha.length <= 9));
            
            const tipoChipValido = semChip || (l.tipo_chip && l.tipo_chip !== '');
            return dddValido && linhaValida && tipoChipValido;
          }).length;
          
          const todasPreenchidas = linhasValidas === quantidade;
          
          return (
            <>
              <span className={`text-xs font-medium ${todasPreenchidas ? 'text-success dark:text-success' : 'text-danger dark:text-danger'}`}>
                {linhasValidas} de {quantidade} completas
              </span>
              {isSaving ? (
                <span className="text-xs text-primary dark:text-primary flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Salvando...
                </span>
              ) : linhasLocal.length > 0 && linhasLocal.some(l => l.ddd || l.linha) && (
                <span className="text-xs text-success dark:text-success flex items-center gap-1">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Salvo
                </span>
              )}
            </>
          );
        })()}
      </div>
      )}

      {/* Renderizar N LinhaCompletaField - Design idêntico */}
      <div className="space-y-4">
        {Array.from({ length: quantidade }).map((_, index) => {
          const linha = linhasLocal[index] || { 
            ddd: '', 
            linha: '',
            gb: null,
            operadora_doadora: null, 
            tipo_chip: null,
            aparelho_valor: null,
            aparelho_modelo: null,
            aparelho_parcelas: null
          };

          // ✅ VERIFICAR SE LINHA ESTÁ COMPLETA
          const dddValido = linha.ddd && linha.ddd.length === 2;
          
          // Para produtos HA, "Número Novo" é válido
          const isNumeroNovo = linha.linha?.toUpperCase() === 'NÚMERO NOVO';
          // ✅ FIBRA: 8 dígitos. Móvel: 8-9 dígitos.
          const linhaValida = isNumeroNovo || (isFIBRA 
            ? (linha.linha && linha.linha.length === 8)
            : (linha.linha && linha.linha.length >= 8));
          
          const tipoChipValido = semChip || (linha.tipo_chip && linha.tipo_chip !== '');
          const linhaCompleta = dddValido && linhaValida && tipoChipValido;

          return (
            <div 
              key={index} 
              className="space-y-2 p-3 rounded-lg border border-border/40 bg-muted/5"
            >
              {quantidade > 1 && (
                <Label className={cn(
                  "text-xs font-medium flex items-center gap-1",
                  linhaCompleta ? "text-success dark:text-success" : "text-danger dark:text-danger"
                )}>
                  <span>Linha {index + 1}</span>
                  {linhaCompleta ? (
                    <span className="text-success">✓</span>
                  ) : (
                    <span className="text-danger"><AlertTriangle className="mr-1 inline h-3 w-3" />Incompleta</span>
                  )}
                </Label>
              )}
              
              <LinhaCompletaField
                ddd={linha.ddd || ''}
                linha={linha.linha || ''}
                gb={linha.gb}
                operadoraDoadora={linha.operadora_doadora}
                tipoChip={linha.tipo_chip}
                isHA={isHA}
                ativacaoChip={ativacaoChip}
                semChip={semChip}
                isFIBRA={isFIBRA}
                semLinhaFixa={semLinhaFixa}
                disabled={disabled}
                // 🆕 Props de aparelho
                aparelhoValor={linha.aparelho_valor}
                aparelhoModelo={linha.aparelho_modelo}
                aparelhoParcelas={linha.aparelho_parcelas}
                onDDDChange={(v) => updateLinha(index, 'ddd', v)}
                onLinhaChange={(v) => updateLinha(index, 'linha', v)}
                onGBChange={(v) => updateLinha(index, 'gb', v)}
                onOperadoraDetectada={(op) => updateLinha(index, 'operadora_doadora', op)}
                onTipoChipChange={(v) => {
                  console.log(`🎯 [TIPO_CHIP] Linha ${index + 1} - Novo valor:`, v);
                  console.log('📊 [LINHA ANTES]:', linha);
                  updateLinha(index, 'tipo_chip', v);
                  console.log('📊 [NOVO VALOR]:', v);
                }}
                onSemLinhaFixaChange={onSemLinhaFixaChange}
                // 🆕 Callbacks de aparelho
                onAparelhoValorChange={(v) => updateLinha(index, 'aparelho_valor', v)}
                onAparelhoModeloChange={(v) => updateLinha(index, 'aparelho_modelo', v)}
                onAparelhoParcelasChange={(v) => updateLinha(index, 'aparelho_parcelas', v)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
